import React, { useCallback, useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import ProjectTitle from '../components/ProjectTitle';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Image, Spinner } from 'react-bootstrap';
import DetailFacility from '../components/DetailFacility';
import DetailBill from '../components/DetailBill';
import { api_url } from '../api/config/config';
import { useApi } from '../api/contexts/ApiContext';
import { Facility, Opportunity } from '../types/type';
import SideButton from '../components/SideButton';
import { resetFacilitiesId } from '../store/user/selectfacilitySlice';

const ProjectDetail: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const backendURL = api_url;

    const { userInfo } = useSelector((state: RootState) => state.auth);
    const { vendorInfo } = useSelector((state: RootState) => state.vendor);

    const [countFacilities, setCountFacilities] = useState(0);
    const [alreadyBid, setAlreadyBid] = useState<boolean | string>(false);
    const [opportunity, setOpportunity] = useState<Opportunity>();
    const [recs, setRecs] = useState<{ [key: string]: Facility[] }>({});
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [isOpenMap, setIsOpenMap] = useState<Record<number, boolean>>({});

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const opportunityId: number | null = queryParams.get('opportunity_id') ? parseInt(queryParams.get('opportunity_id')!) : null;
    const { getModule } = useApi();

    const toggleOpen = (id: number) => {
        setIsOpenMap(prev => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const handleBid = () => {
        // 案件切替のたびに“前案件の選択”をクリア
        dispatch(resetFacilitiesId());
        
        if (countFacilities === 1) {
            navigate(`/bid?opportunity_id=${opportunityId}`);
        } else {
            navigate(`/bid_start?opportunity_id=${opportunityId}`);
        }
    };

    const fetchOpportunity = useCallback(async () => {
        try {
            const opportunityApi = await getModule('OpportunityApi');
            const response = await opportunityApi.getOpportunity({ opportunity_id: opportunityId });
            if (response && response.data !== undefined) {
                setOpportunity(response.data as Opportunity);
            }
        } catch (error) {
            console.error('Error: ', error);
        }
    }, [opportunityId, getModule]);

    const fetchFacilityByOpportunityId = useCallback(async () => {
        try {
            const FacilityApi = await getModule('FacilityApi');
            if (opportunityId) {
                const response = await FacilityApi.getFacilityByOpportunityId({ opportunity_id: opportunityId });
                const facilities: any = response.data;

                const tmp_recs: { [key: string]: Facility[] } = {};

                facilities.forEach((rec: Facility) => {
                    const supplierName = rec.supplier?.name;
                    if (supplierName) {
                        if (!tmp_recs[supplierName]) {
                            tmp_recs[supplierName] = [];
                        }
                        tmp_recs[supplierName].push(rec);
                    }
                });

                setRecs(tmp_recs);
                setCountFacilities(facilities.length);
            }
        } catch (error) {
            console.error('Error: ', error);
        }
    }, [opportunityId, getModule]);

    const fetchHasAlreadyBid = useCallback(async () => {
        try {
            if (userInfo?.id && opportunityId) {
                const bidApi = await getModule('BidApi');
                const body = {
                    user_id: userInfo.id,
                    opportunity_id: opportunityId,
                    vendor_id: vendorInfo?.vendor_id
                }
                const response = await bidApi.hasAlreadyBid(body);
                setAlreadyBid(response.data as boolean | string);
            }
        } catch (error) {
            console.error('Error:', error);
        }
    }, [userInfo, opportunityId, getModule]);

    useEffect(() => {
        if (!opportunityId) {
            navigate('/');
            return;
        }

        fetch(`${api_url}/bidVendorView?user_id=${userInfo?.id}&opportunity_id=${opportunityId}`, {
            method: 'GET',
            headers: { "Content-Type": "application/json" },
        });

        fetchOpportunity();
    }, [opportunityId, navigate, fetchOpportunity]);

    useEffect(() => {
        setLoading(true);
        Promise.all([fetchFacilityByOpportunityId(), fetchHasAlreadyBid()]).then(() => {
            setLoading(false);
        });
    }, [fetchFacilityByOpportunityId, fetchHasAlreadyBid]);

    return (
        <>

            <section className="section-name project-details-section">
                <div className="container">
                    {loading ? <div className="d-flex justify-content-center align-items-center mb-5" style={{ height: '100%' }}>
                        <Spinner animation="border" variant="primary" />
                    </div> : (
                        <>
                            {opportunity && <ProjectTitle company={opportunity} />}
                            {Object.entries(recs).map(([supplier_name, facilities]) => (
                                <div key={supplier_name} className="bg-ass-demo p-4 mb-5">
                                    <div className="row">
                                        <div className="col-lg-5 col-md-7 col-10 mx-auto py-3 mb-3">
                                            <div className="bg-white text-center rounded-pill text-sm py-1 fw-300">
                                                <Image className="img img-fluid height-25 mb-1 me-2" src="assets/images/other/icon-poles.svg" />
                                                {supplier_name}管轄
                                            </div>
                                        </div>
                                    </div>
                                    {facilities.map((facility) => (
                                        <div key={facility.id}>
                                            <div className="my-1 pt-1 bg-white">
                                                {facility && opportunity && (
                                                    <DetailFacility facility={facility} company={opportunity} isOpen={!isOpenMap[facility.id]}
                                                        onToggle={() => toggleOpen(facility.id)} />
                                                )}
                                            </div>
                                            <div className="bg-white pb-3">
                                                {facility && opportunity && (
                                                    <DetailBill facility={facility} opportunity={opportunity} isOpen={!isOpenMap[facility.id]} />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </>
                    )}
                </div>

                <div className="text-center mb-lg-4 mb-4">
                    {alreadyBid ? (
                        <Link id="bidProgress" to={`/bid_progress?opportunity_id=${opportunityId}`}>
                            <Button id="bidProgress" type="button" className="btn-lg btn-theme btn-danger rounded-pill px-lg-5 px-3 mr-2">
                                <span className="mx-3">応札状況を確認する</span>
                            </Button>
                        </Link>
                    ) : (
                        <Button id="bid" type="button" className="btn-lg btn-theme btn-danger rounded-pill px-lg-5 px-3 mr-2" onClick={handleBid}>
                            <span className="mx-3">応札する</span>
                        </Button>
                    )}
                    <Link id="contact" to={`/contact?opportunity_id=${opportunityId}`}>
                        <Button id="contact" type="button" className="btn-lg btn-theme btn-danger rounded-pill px-lg-5 px-3 mr-2">
                            <span className="mx-3">問い合わせする</span>
                        </Button>
                    </Link>
                </div>
            </section>
            <SideButton opportunityId={opportunityId} />
            <Footer />
        </>
    );
};

export default ProjectDetail;